import React, { useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option, Constants } from '@/src/utils';
import {
    type StandardsReportFiltersProps,
    type InitialOptions,
    type SchoolClassOptions,
    type ExamOption,
    type ClassOption,
    type StandardOption,
} from '../models/standardsReportFilterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    SimpleMultipleSelect,
    FilterBarContainer,
} from '@/src/ui';

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectClassOption = getZeroOption('Select Class');
const NoClassOption = getZeroOption('No Classes Available');
const AllStudentsOption = getZeroOption('All');

// Scalar required filters — examIds is required separately since it's an array.
const requiredScalarFields = ['districtId', 'schoolId', 'gradeLevelMasterId', 'classId'] as const;
function getMissingFields(fs: any): string[] {
    const missing: string[] = [];
    for (const key of requiredScalarFields) {
        const val = Number(fs[key] ?? 0);
        if (!val) missing.push(Constants.fieldLabels[key] || key);
    }
    if (!Array.isArray(fs.examIds) || fs.examIds.length === 0) {
        missing.push(Constants.fieldLabels.examId || 'Assessments');
    }
    return missing;
}

const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
    loading
        ? { options: [LoadingOption], value: 0, disabled: true }
        : { options: opts, value: val };

const StandardsReportFilters: React.FC<StandardsReportFiltersProps> = ({
    filterState,
    setFilterState,
    refreshTableData,
    onExamNamesChange,
    showStudentFilter = true,
}) => {
    const [error, setError] = useState('');
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeLevelOptions, setGradeLevelOptions] = useState<Option[]>([LoadingOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [examTypeOptions, setExamTypeOptions] = useState<Option[]>([AllOption]);
    const [studentOptions, setStudentOptions] = useState<Option[]>([AllStudentsOption]);

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const [allExamOptions, setAllExamOptions] = useState<ExamOption[] | null>(null);
    const [examOptions, setExamOptions] = useState<Option[]>([]);
    const [examLoading, setExamLoading] = useState(false);

    const [standardsOptions, setStandardsOptions] = useState<Option[]>([]);
    const [standardsLoading, setStandardsLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setDistrictOptions([
                    SelectDistrictOption,
                    ...(data.districtOptions ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setGradeLevelOptions([SelectGradeOption, ...(data.gradeLevelMasterOptions ?? [])]);
                setExamTypeOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setError('');
            } catch {
                setError(
                    'Failed to fetch initial filter options. Please refresh the page or try again.'
                );
            } finally {
                setInitialLoadComplete(true);
                if (filterState.classId) {
                    fetchStudentOptions(filterState.classId);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFilterState]);

    useEffect(() => {
        if (
            initialLoadComplete &&
            filterState &&
            filterState.districtId &&
            filterState.districtId !== Number(SelectDistrictOption.id)
        ) {
            fetchSchoolClassOptions(filterState.districtId).then(() => {
                setFiltersLoading(false);
            });
        } else if (initialLoadComplete) {
            setFiltersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.districtId]);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    const fetchSchoolClassOptions = async (districtId: number) => {
        if (!districtId || districtId === Number(SelectDistrictOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                gradeLevelMasterId: Number(SelectGradeOption.id),
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const newSchoolOptions = (data.schoolOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);
            setClassData(data.classOptions ?? []);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => {
                const schoolExists = newSchoolOptions.some(
                    (opt) => Number(opt.id) === Number(prev.schoolId)
                );
                return {
                    ...prev,
                    schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                };
            });
            setError('');
        } catch {
            setError('Failed to fetch schools/classes. Please refresh the page or try again.');
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
    };

    const updateClassOptions = (gradeLevelId: number, schoolId: number) => {
        if (
            !gradeLevelId ||
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id) ||
            gradeLevelId === Number(SelectGradeOption.id)
        ) {
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(SelectClassOption.id) }));
            return;
        }

        const filtered = classData
            .filter((c) => c.schoolId === schoolId)
            .filter(
                (c) =>
                    !c.gradeLevelMasterIds?.length || c.gradeLevelMasterIds.includes(gradeLevelId)
            )
            .map((c) => ({ id: c.id, name: c.name }));

        if (!filtered.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([SelectClassOption, ...filtered]);
            setFilterState((prev) => {
                const classExists = filtered.some((opt) => Number(opt.id) === Number(prev.classId));
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(SelectClassOption.id),
                };
            });
        }
    };

    // --- Exam Pipeline ---
    const maybeFetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setAllExamOptions(null);
            setExamLoading(false);
            return;
        }

        setExamLoading(true);
        try {
            const res = await apiRequest<ExamOption[]>(
                'GET',
                `utility/filterData/assignedExams?DistrictId=${filterState.districtId}&SchoolId=${filterState.schoolId}&GradeLevelMasterId=${gradeLevelMasterId}&ClassId=${filterState.classId ?? 0}`
            );

            const data = (res.data ?? []).filter((exam) => exam.schoolIds?.length);
            const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
            setAllExamOptions(sorted);
            setError('');
        } catch {
            setAllExamOptions([]);
            setError('Failed to load assessment options. Please refresh the page or try again.');
        } finally {
            setExamLoading(false);
        }
    };

    const filterExamsLocally = (
        classId: number,
        examTypeMasterId: number,
        allExamsData: ExamOption[] | null
    ) => {
        if (!allExamsData) {
            setExamOptions([]);
            return;
        }

        let filtered = allExamsData;

        if (classId && classId !== Number(SelectClassOption.id)) {
            filtered = filtered.filter((e) => !e.classIds?.length || e.classIds.includes(classId));
        }

        if (examTypeMasterId && examTypeMasterId !== Number(AllOption.id)) {
            filtered = filtered.filter((e) => e.examTypeMasterId === examTypeMasterId);
        }

        const newOptions: Option[] = filtered.map((e) => ({ id: e.id, name: e.name }));
        setExamOptions(newOptions);

        // Prune any selected examIds that are no longer in the filtered list.
        setFilterState((prev) => {
            const validIds = new Set(newOptions.map((o) => Number(o.id)));
            const pruned = (prev.examIds ?? []).filter((id) => validIds.has(Number(id)));
            if (pruned.length === (prev.examIds ?? []).length) return prev;
            return { ...prev, examIds: pruned };
        });
    };

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.gradeLevelMasterId, filterState.classId]);

    useEffect(() => {
        filterExamsLocally(filterState.classId, filterState.examTypeMasterId, allExamOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allExamOptions, filterState.classId, filterState.examTypeMasterId]);

    // --- Standards Pipeline ---
    // Fetches the set of standards present across all selected assessments.
    const fetchStandardsOptions = async (examIds: number[]) => {
        if (!examIds || examIds.length === 0) {
            setStandardsOptions([]);
            setStandardsLoading(false);
            return;
        }
        setStandardsLoading(true);
        try {
            const examIdsQuery = examIds.map((id) => `examIds=${id}`).join('&');
            const res = await apiRequest<{ standardOptions: StandardOption[] | null }>(
                'GET',
                `utility/filterData?getStandard=true&${examIdsQuery}`
            );
            const sorted = (res.data?.standardOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setStandardsOptions(sorted);

            // Prune any previously-selected standards that are no longer available.
            setFilterState((prev) => {
                const validIds = new Set(sorted.map((o) => Number(o.id)));
                const pruned = (prev.standardsIds ?? []).filter((id) => validIds.has(Number(id)));
                if (pruned.length === (prev.standardsIds ?? []).length) return prev;
                return { ...prev, standardsIds: pruned };
            });
            setError('');
        } catch {
            setStandardsOptions([]);
            setError('Failed to load standards options. Please refresh the page or try again.');
        } finally {
            setStandardsLoading(false);
        }
    };

    // Refetch standards whenever the set of selected assessments changes.
    // Stringify so the effect doesn't fire on every render due to array identity.
    const examIdsKey = (filterState.examIds ?? []).join(',');
    useEffect(() => {
        const ids = examIdsKey
            ? examIdsKey
                  .split(',')
                  .map(Number)
                  .filter((n) => n > 0)
            : [];
        fetchStandardsOptions(ids);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examIdsKey]);

    // Notify parent when the set of selected assessment NAMES changes.
    useEffect(() => {
        if (!onExamNamesChange) return;
        const selectedNames = (filterState.examIds ?? [])
            .map((id) => examOptions.find((o) => Number(o.id) === Number(id))?.name)
            .filter((n): n is string => !!n);
        onExamNamesChange(selectedNames);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examIdsKey, examOptions]);

    const fetchStudentOptions = async (classId: number) => {
        if (!classId || classId === Number(SelectClassOption.id)) {
            setStudentOptions([AllStudentsOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentsOption.id) }));
            return;
        }
        try {
            const res = await apiRequest<{ studentOptions: Option[] }>(
                'GET',
                `utility/filterData?getStudentsByClass=true&classId=${classId}`
            );
            const data = res.data.studentOptions ?? [];
            setStudentOptions([AllStudentsOption, ...data]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentsOption.id) }));
        } catch {
            setStudentOptions([AllStudentsOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentsOption.id) }));
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const districtId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            districtId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                            examIds: [],
                            standardsIds: [],
                            studentId: Number(AllStudentsOption.id),
                            examTypeMasterId: Number(AllOption.id),
                        });
                        setSchoolOptions([SelectSchoolOption]);
                        setClassOptions([SelectClassOption]);
                        setExamOptions([]);
                        setStandardsOptions([]);
                        setAllExamOptions(null);
                        fetchSchoolClassOptions(districtId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const schoolId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            schoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                            examIds: [],
                            standardsIds: [],
                            studentId: Number(AllStudentsOption.id),
                        });
                        setAllExamOptions(null);
                        updateClassOptions(Number(SelectGradeOption.id), schoolId);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelOptions,
                                gradeLevelOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const gradeId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            gradeLevelMasterId: gradeId,
                            classId: Number(SelectClassOption.id),
                            examIds: [],
                            standardsIds: [],
                        });
                        updateClassOptions(gradeId, filterState.schoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class *"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const classId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            classId,
                            studentId: Number(AllStudentsOption.id),
                            examIds: [],
                            standardsIds: [],
                        });
                        fetchStudentOptions(classId);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const examTypeId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            examTypeMasterId: examTypeId,
                            examIds: [],
                            standardsIds: [],
                        });
                    }}
                    options={examTypeOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading}
                />
                <SimpleMultipleSelect
                    name="exams-select"
                    label="Assessments *"
                    value={filterState.examIds ?? []}
                    onChange={(values) => {
                        setFieldsChanged(true);
                        setFilterState({
                            ...filterState,
                            examIds: values.map(Number),
                            // Standards depend on the selected exams; clear so they
                            // get repopulated by the standards-options fetch.
                            standardsIds: [],
                        });
                    }}
                    options={examOptions}
                    sx={{ minWidth: 220, maxWidth: 360 }}
                    disabled={
                        examLoading ||
                        filtersLoading ||
                        filterState.classId === Number(SelectClassOption.id) ||
                        examOptions.length === 0
                    }
                />
                <SimpleButton
                    variant="outlined"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                    onClick={() => {
                        const availableIds = examOptions.map((opt) => Number(opt.id));
                        const allSelected =
                            availableIds.length > 0 &&
                            availableIds.every((id) => (filterState.examIds ?? []).includes(id));
                        setFieldsChanged(true);
                        setFilterState((prev) => ({
                            ...prev,
                            examIds: allSelected ? [] : availableIds,
                            // Always clear standards: either we just dropped exams,
                            // or we just expanded the set — either way the standards
                            // list needs to be refetched.
                            standardsIds: [],
                        }));
                    }}
                    disabled={
                        examLoading ||
                        filtersLoading ||
                        examOptions.length === 0 ||
                        filterState.classId === Number(SelectClassOption.id)
                    }
                >
                    {(() => {
                        const availableIds = examOptions.map((opt) => Number(opt.id));
                        const allSelected =
                            availableIds.length > 0 &&
                            availableIds.every((id) => (filterState.examIds ?? []).includes(id));
                        return allSelected ? 'Deselect All Assessments' : 'Select All Assessments';
                    })()}
                </SimpleButton>
            </FilterBarContainer>
            <FilterBarContainer>
                <SimpleMultipleSelect
                    name="standards-select"
                    label="Standards"
                    value={filterState.standardsIds ?? []}
                    onChange={(values) => {
                        setFieldsChanged(true);
                        setFilterState({
                            ...filterState,
                            standardsIds: values.map(Number),
                        });
                    }}
                    options={standardsOptions}
                    sx={{ minWidth: 220, maxWidth: 360 }}
                    disabled={
                        standardsLoading ||
                        filtersLoading ||
                        (filterState.examIds ?? []).length === 0 ||
                        standardsOptions.length === 0
                    }
                />
                <Tooltip
                    title="Leaving Standards empty returns results for every standard — the same as selecting all."
                    arrow
                >
                    {/* span wrapper lets the tooltip show even when the button is disabled */}
                    <span style={{ alignSelf: 'center' }}>
                        <SimpleButton
                            variant="outlined"
                            sx={{ minHeight: 40, height: 40 }}
                            onClick={() => {
                                setFieldsChanged(true);
                                setFilterState((prev) => ({ ...prev, standardsIds: [] }));
                            }}
                            disabled={
                                standardsLoading ||
                                filtersLoading ||
                                (filterState.standardsIds ?? []).length === 0
                            }
                        >
                            Deselect All Standards
                        </SimpleButton>
                    </span>
                </Tooltip>
                {showStudentFilter && (
                    <SimpleSingleSelect
                        name="student-select"
                        label="Student"
                        value={filterState.studentId}
                        onChange={(e) => {
                            setFieldsChanged(true);
                            setFilterState({ ...filterState, studentId: Number(e.target.value) });
                        }}
                        options={studentOptions}
                        sx={{ minWidth: 220, maxWidth: 280 }}
                        disabled={filtersLoading}
                    />
                )}
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setError(
                                `Please select the following required fields before filtering: ${missing.join(', ')}.`
                            );
                            return;
                        }
                        setError('');
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            <ErrorMessage error={error} />
        </Box>
    );
};

export default StandardsReportFilters;
